<template lang="pug">
  button.gl-btn(
    :class="{ ['gl-btn--' + options.size]: options.size, ['gl-btn--' + options.style]: options.style, ['gl-btn--' + options.location]: options.location }"
    :disabled="options.isLoading || options.isDisabled"
    :type="options.type"
    :title="options.isLoading ? 'Loading...' : options.label"
    v-gl-wl="{ apply: (options.style === 'brand') ? [ 'brandBtnTextColor', 'brandBtnBgColor', 'brandFontWeight' ] : '' }"
    layout="row"
    layout-align="center center")

    // loading
    pulse-loader(:loading="options.isLoading || false" :size="'10px'")

    // fa
    font-awesome-icon.gl-btn__fa(v-if="options.fa" :icon="options.fa")

    // label
    span.gl-btn__label(v-if="options.label") {{ options.label }}
</template>

<script>
export default {
  props: {
    options: Object
  }
}
</script>

<style lang="scss">
  @import '../styles/colors';
  @import '../styles/variables';

  .gl-btn {

    // each type
    &--brand,
    &--success,
    &--danger,
    &--outline,
    &--neutral,
    &--gradient,
    &--smoke,
    &--transparent,
    &--brand-settings,
    &--brand-outline,
    &--danger-outline,
    &--success-outline {
      font-size: 16px;
      padding: 12px 14px;
      font-weight: 500;
      line-height: 1;
      border-radius: 4px;
      min-height: 34px;
      text-align: center;
      transition: all 0.15s;
      text-transform: none;
      cursor: pointer;
      white-space: nowrap;
      font-family: $gl-font-family;
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .v-spinner .v-pulse {
        background-color: $gl-white !important;
      }
    }

    &__label {
      &.icon-left, {
        margin-left: 8px;
      }
      &.icon-right, {
        margin-right: 8px;
      }
    }

    &__loading {
      margin-right: 10px;
    }

    &--brand, &--success, &--danger, &--brand-settings {
      color: $gl-white-smoke;
      &:disabled {
        color: $gl-white;
      }
    }

    &--brand {
      background-color: $gl-blue;
      box-shadow: $gl-box-shadow;
      &:not([disabled]):hover {
        opacity: 0.8;
      }
      &:disabled {
        background-color: $gl-blue;
      }
    }

    &--brand-settings {
      border-radius: 0.25rem;
      background-color: $gl-blue;
      border: 1px solid $gl-blue;
      box-shadow: $gl-box-shadow;
      &:not([disabled]):hover {
        border-color: darken($gl-blue, 8%);
        background-color: darken($gl-blue, 8%);
      }
      &:disabled {
        background-color: $gl-blue;
      }
    }

    &--brand-outline {
      border-radius: 3px;
      background-color: transparent;
      border: 1px solid #C4CDD5;;
      color: $gl-blue;
      box-shadow: $gl-box-shadow-light;
      &:not([disabled]):hover {
        box-shadow: $gl-box-shadow;
      }

      .v-spinner .v-pulse {
        background-color: $gl-grey !important;
      }
    }

    &--success {
      background-color: $gl-green;
      border: 1px solid #38A638;
      &:not([disabled]):hover {
        background-color: darken($gl-green, 5%);
        border-color: darken(#38A638, 5%);
      }
      &:disabled {
        background-color: $gl-green !important;
      }
    }

    &--danger {
      background-color: $gl-red;
      border: 1px solid $gl-red;
      &:not([disabled]):hover {
        background-color: darken($gl-red, 5%);
        border-color: darken($gl-red, 5%);
      }
      &:disabled {
        background-color: $gl-red !important;
      }
    }

    &--danger-outline {
      border-radius: 3px;
      background-color: transparent;
      border: 1px solid #C4CDD5;
      color: #FE544B;
      box-shadow: $gl-box-shadow-light;
      &:not([disabled]):hover {
        box-shadow: $gl-box-shadow;
      }

      .v-spinner .v-pulse {
        background-color: $gl-grey !important;
      }
    }

    &--success-outline {
      border-radius: 3px;
      background-color: transparent;
      border: 1px solid #C4CDD5;
      color: $gl-green;
      box-shadow: $gl-box-shadow-light;
      &:not([disabled]):hover {
        box-shadow: $gl-box-shadow;
      }

      .v-spinner .v-pulse {
        background-color: $gl-grey !important;
      }
    }

    &--outline {
      color: $gl-grey-darker;
      background-color: transparent;
      border: 1px solid #C4CDD5;
      box-shadow: $gl-box-shadow-light;
      &:not([disabled]):hover {
        box-shadow: $gl-box-shadow;
      }

      .v-spinner .v-pulse {
        background-color: $gl-grey !important;
      }
    }

    &--neutral {
      background: transparent;
      border: 1px solid $gl-grey-border;
      color: $gl-grey-dark !important;
      &:not([disabled]):hover {
        background-color: $gl-grey-hover;
        color: $gl-grey-darker !important;
      }
      &:focus {
        color: $gl-grey-darker;
      }
    }

    &--transparent {
      background: transparent;
      border: none;
      color: $gl-grey-darker;
      &:not([disabled]):hover {
        background-color: $gl-grey-hover;
      }
      &:focus {
        color: $gl-grey-darker;
      }
    }

    &--smoke {
      background-image: linear-gradient(-180deg, $gl-white 0%, $gl-white-smoke 100%);
      box-shadow: $gl-box-shadow-light;
      border: 1px solid $gl-grey-border;
      color: $gl-grey-darker;
      &:not([disabled]):hover {
        background-image: linear-gradient(-180deg, $gl-white 0%, $gl-grey-smoke 100%);
      }
    }

    &--gradient {
      background: $gl-gradient;
      border: 1px solid $gl-grey-border;
      box-shadow: $gl-box-shadow-light;
      &:disabled {
        opacity: 0.7;
      }
      &:not([disabled]):hover {
        color: $gl-grey-darker;
        background: $gl-grey-hover;
        border-color: $gl-grey-border-dark;
      }

      .v-spinner .v-pulse {
        background-color: $gl-grey-darker !important;
      }
    }

    &--small {
      padding: 6px 8px;
      font-weight: 700;
      font-size: 15px;
      min-height: 0;
    }

    &--large {
      font-size: rem(1.8);
      font-weight: 700;
      padding: 12px 14px;
    }

    &--small-when-screen-small {

      @media(max-width: 800px) {
        padding: 6px 10px;
        font-weight: 600;
        font-size: 13px !important;
      }
    }

    &--full-width {
      font-size: 18px;
      width: 100%;
      font-weight: 600;
    }

    &--nav {
      position: fixed;
      right: 88px;
      top: 14px;
      z-index: 101;
      height: 40px;
    }

    &__material-left {
      font-size: 20px;
      max-width: 20px;
    }

    .v-spinner {
      margin-right: 12px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/gl-btn.vue